import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import {
  deleteObject,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';

import config from '../../envConfig';
import { FileVisibility } from '../../shared/constants';
import { auth, waitForAuthInit } from '../../shared/firebase/firebase-client';
import { IAppFile } from '../../types/interfaces/app-file';
import { INewFile } from '../../types/interfaces/new-file';
import { dataUrlToBlob } from './file-conversion-utils';

function normalizeFirebaseStorageBucket(bucket: string): string {
  return bucket.replace(/^gs:\/\//, '').replace(/\/+$/, '');
}

/**
 * Native @capacitor-firebase/authentication signs in only the native layer by default.
 * When the Firebase JS SDK has no `auth.currentUser`, Storage uploads still work by
 * sending the plugin ID token to the Firebase Storage REST API.
 */
async function uploadFirebaseStorageWithNativePluginToken(
  file: Blob,
  fullStoragePath: string,
  contentType: string,
): Promise<void> {
  const { token } = await FirebaseAuthentication.getIdToken({
    forceRefresh: true,
  });

  const bucket = normalizeFirebaseStorageBucket(
    config.firebaseStorageBucket || '',
  );
  const nameParam = encodeURIComponent(fullStoragePath);
  const url = `https://firebasestorage.googleapis.com/v0/b/${encodeURIComponent(
    bucket,
  )}/o?name=${nameParam}&uploadType=media`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': contentType,
    },
    body: file,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Firebase Storage REST upload failed (${response.status}): ${text}`,
    );
  }
}

async function deleteFirebaseStorageWithNativePluginToken(
  objectPath: string,
): Promise<void> {
  const { token } = await FirebaseAuthentication.getIdToken({
    forceRefresh: true,
  });

  const bucket = normalizeFirebaseStorageBucket(
    config.firebaseStorageBucket || '',
  );
  const encodedPath = encodeURIComponent(objectPath);
  const url = `https://firebasestorage.googleapis.com/v0/b/${encodeURIComponent(
    bucket,
  )}/o/${encodedPath}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok && response.status !== 404) {
    const text = await response.text();
    throw new Error(
      `Firebase Storage REST delete failed (${response.status}): ${text}`,
    );
  }
}

async function generateFileNameHash(
  originalFileName: string,
  timestamp: number,
): Promise<string> {
  const randomStr = Math.random().toString(36).substring(2, 15);
  const data = `${originalFileName}-${timestamp}-${randomStr}`;

  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  const extension = originalFileName.split('.').pop() || '';
  return `${hashHex.substring(0, 32)}.${extension}`;
}

export async function uploadFileToFirebase(
  file: File | Blob,
  storagePath: string,
  /** Firebase Auth UID (storage path segment). */
  userUid: string,
  visibility: FileVisibility = FileVisibility.public,
  isPreview = false,
  originalFileName?: string,
): Promise<string> {
  await waitForAuthInit();
  const timestamp = Date.now();

  const fileName =
    originalFileName ||
    (file instanceof File ? file.name : `file-${timestamp}`);
  const hashedFileName = await generateFileNameHash(fileName, timestamp);

  const pathParts = [storagePath, userUid, visibility];
  if (isPreview) {
    pathParts.push('preview');
  }
  pathParts.push(hashedFileName);

  const fullStoragePath = pathParts.join('/');

  const metadata = {
    cacheControl: 'public, max-age=604800',
    contentType: file instanceof File ? file.type : 'application/octet-stream',
  };

  const isNativePlatform = Capacitor.getPlatform() !== 'web';
  const pluginUser = isNativePlatform
    ? (await FirebaseAuthentication.getCurrentUser()).user
    : null;

  if (auth.currentUser) {
    const storage = getStorage(auth.app);
    const storageRef = ref(storage, fullStoragePath);

    await new Promise<void>((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        'state_changed',
        () => {},
        (error) => {
          reject(error);
        },
        () => {
          resolve();
        },
      );
    });
  } else if (pluginUser) {
    await uploadFirebaseStorageWithNativePluginToken(
      file,
      fullStoragePath,
      metadata.contentType,
    );
  } else {
    throw new Error('Not authenticated for Firebase Storage');
  }

  return fullStoragePath;
}

export async function uploadFiles(
  file: File | Blob,
  storagePath: string,
  /** Firebase Auth UID — storage path segment. */
  storageUserUid: string,
  visibility: FileVisibility = FileVisibility.public,
  previewFile?: File | Blob,
  originalFileName?: string,
  previewOriginalFileName?: string,
): Promise<{ storagePath: string; previewStoragePath?: string }> {
  const mainStoragePath = await uploadFileToFirebase(
    file,
    storagePath,
    storageUserUid,
    visibility,
    false,
    originalFileName,
  );

  let previewStoragePath: string | undefined;
  if (previewFile) {
    previewStoragePath = await uploadFileToFirebase(
      previewFile,
      storagePath,
      storageUserUid,
      visibility,
      true,
      previewOriginalFileName || originalFileName,
    );
  }

  return {
    storagePath: mainStoragePath,
    previewStoragePath,
  };
}

export async function deleteFileFromFirebase(
  storagePath: string,
): Promise<void> {
  const isNativePlatform = Capacitor.getPlatform() !== 'web';
  const pluginUser = isNativePlatform
    ? (await FirebaseAuthentication.getCurrentUser()).user
    : null;

  if (auth.currentUser) {
    const storage = getStorage(auth.app);
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    return;
  }

  if (pluginUser) {
    await deleteFirebaseStorageWithNativePluginToken(storagePath);
    return;
  }

  throw new Error('Not authenticated for Firebase Storage delete');
}

export async function deleteFilesFromFirebase(
  storagePaths: string[],
): Promise<void> {
  await Promise.all(
    storagePaths.map((path) => deleteFileFromFirebase(path).catch(() => {})),
  );
}

export async function uploadFileAndCreateAppFile(
  file: INewFile,
  storagePath: string,
  /** Firebase Auth UID — segment in Storage path (GET /users/me `uid`). */
  storageUserUid: string,
  /** Application user id — `IAppFile.ownerId`. */
  ownerId: string,
  visibility: FileVisibility = FileVisibility.public,
): Promise<IAppFile> {
  const fileBlob = dataUrlToBlob(file.fileBlob);
  const previewBlob = file.previewFileBlob
    ? dataUrlToBlob(file.previewFileBlob)
    : undefined;

  const { storagePath: uploadedStoragePath, previewStoragePath } =
    await uploadFiles(
      fileBlob,
      storagePath,
      storageUserUid,
      visibility,
      previewBlob,
      file.name,
    );

  const uploadedFile: IAppFile = {
    id: file.id?.toString(),
    name: file.name,
    mimeType: file.mimeType,
    size: file.size,
    ownerId,
    createdAt: file.createdAt,
    storageLink: uploadedStoragePath,
    previewStorageLink: previewStoragePath,
  };

  return uploadedFile;
}
